import { useCallback } from 'react'
import { NavigateFunction } from 'react-router-dom'
import { AppDispatch } from 'src/store/store'
import { uploadImage } from 'src/v3/utilities/image-upload'
import {
  createCourseByLinkedServiceID,
  updateCourseCore,
  updateCourseSyllabus,
  getCourseImageUploadUrl,
} from '../../utils/api'
import { fetchCourses, fetchSelectedCourse } from '../../create.slice'
import { CourseV5Details, ObjectivesReference } from '../types'
import { coreInfoSchema, syllabusSchema } from '../validation'
import { CREATE_COURSE_URLS } from '../../constants'

type CourseFormValues = {
  title: string
  linkedServiceID: string
  sectionID: string
  templateID: string
  length: number
  description: string
  objectives: ObjectivesReference[]
  references: ObjectivesReference[]
  banner: string
  thumbnail: string
}

type UseCourseFormSubmissionProps = {
  isEditMode: boolean
  courseID?: string
  courseDetails: CourseV5Details | null
  banner?: File
  thumbnail?: File
  dispatch: AppDispatch
  navigate: NavigateFunction
  showSuccess: (message: string) => void
  showError: (message: string) => void
}

export function useCourseFormSubmission({
  isEditMode,
  courseID,
  courseDetails,
  banner,
  thumbnail,
  dispatch,
  navigate,
  showSuccess,
  showError,
}: UseCourseFormSubmissionProps) {
  let validateSection = useCallback(
    (section: 'core' | 'syllabus', values: CourseFormValues): boolean => {
      if (section === 'core') {
        let coreValidation = coreInfoSchema.safeParse({
          linkedServiceID: values.linkedServiceID,
          sectionID: values.sectionID,
          title: values.title,
          length: values.length,
          templateID: values.templateID,
        })

        if (!coreValidation.success) {
          showError(
            'Please complete all required fields in the Core Information section.',
          )
          return false
        }
      }

      if (section === 'syllabus') {
        let syllabusValidation = syllabusSchema.safeParse({
          description: values.description,
          objectives: values.objectives,
          references: values.references,
        })

        if (!syllabusValidation.success) {
          showError(
            'Please complete all required fields in the Syllabus section.',
          )
          return false
        }
      }

      return true
    },
    [showError],
  )

  let saveCourse = useCallback(
    async (
      values: CourseFormValues,
      hasCoreChanges: boolean,
      hasSyllabusChanges: boolean,
    ): Promise<{ id: string }> => {
      if (!hasCoreChanges && !hasSyllabusChanges) {
        return Promise.resolve({ id: courseID! })
      }

      if (!isEditMode) {
        // Create mode: Always use POST with full payload
        return createCourseByLinkedServiceID(values.linkedServiceID, {
          name: values.title,
          sectionID: values.sectionID,
          templateID: values.templateID,
          length: values.length,
          description: values.description,
          objectives: values.objectives,
          references: values.references,
        })
      }

      // Edit mode
      if (hasCoreChanges && hasSyllabusChanges) {
        // Both sections changed - make two PATCH calls sequentially
        return updateCourseCore(courseID!, {
          name: values.title,
          sectionID: values.sectionID,
          templateID: values.templateID,
          length: values.length,
        }).then(() =>
          updateCourseSyllabus(courseID!, {
            description: values.description,
            objectives: values.objectives,
            references: values.references,
          }),
        )
      }

      if (hasCoreChanges) {
        // Only core changed
        return updateCourseCore(courseID!, {
          name: values.title,
          sectionID: values.sectionID,
          templateID: values.templateID,
          length: values.length,
        })
      }

      // Only syllabus changed
      return updateCourseSyllabus(courseID!, {
        description: values.description,
        objectives: values.objectives,
        references: values.references,
      })
    },
    [isEditMode, courseID, courseDetails],
  )

  let uploadImages = useCallback(
    async (
      uploadCourseID: string,
      shouldUploadImages: boolean,
    ): Promise<string> => {
      if (!shouldUploadImages) {
        return Promise.resolve(uploadCourseID)
      }

      // Build image upload promises functionally
      let imagePromises = [
        ...(banner
          ? [
              uploadImage(banner, () =>
                getCourseImageUploadUrl({
                  id: uploadCourseID,
                  type: 'banner',
                  contentType: banner.type,
                }),
              ),
            ]
          : []),
        ...(thumbnail
          ? [
              uploadImage(thumbnail, () =>
                getCourseImageUploadUrl({
                  id: uploadCourseID,
                  type: 'thumbnail',
                  contentType: thumbnail.type,
                }),
              ),
            ]
          : []),
      ]

      return imagePromises.length > 0
        ? Promise.all(imagePromises).then(() => uploadCourseID)
        : Promise.resolve(uploadCourseID)
    },
    [banner, thumbnail],
  )

  let handleSuccess = useCallback(
    async (finalCourseID: string, dirtySections: string[]) => {
      // Update success message based on what was saved
      let savedSectionsText =
        dirtySections.length === 3
          ? 'Course'
          : dirtySections
              .map((s) => {
                if (s === 'core') return 'Course information'
                if (s === 'images') return 'Images'
                if (s === 'syllabus') return 'Syllabus'
                return s
              })
              .join(', ')

      showSuccess(
        isEditMode
          ? `${savedSectionsText} updated successfully`
          : 'Course created successfully',
      )

      if (!isEditMode) {
        navigate(`${CREATE_COURSE_URLS.INDEX}/${finalCourseID}/edit`)
      } else {
        // Refresh course details in edit mode
        dispatch(fetchSelectedCourse(courseID!))
      }
    },
    [isEditMode, courseID, dispatch, navigate, showSuccess],
  )

  let handleError = useCallback(
    (error: any) => {
      // eslint-disable-next-line no-console
      console.error('Course save failed:', error)
      showError(
        error?.response?.data?.message ||
          error?.message ||
          'Error saving course.',
      )
    },
    [showError],
  )

  let handleSubmit = useCallback(
    async (
      values: CourseFormValues,
      sectionsToSave: string[],
    ): Promise<void> => {
      let hasCoreChanges = sectionsToSave.includes('core')
      let hasSyllabusChanges = sectionsToSave.includes('syllabus')
      let hasImageChanges = sectionsToSave.includes('images')

      // Validate sections
      if (hasCoreChanges && !validateSection('core', values)) {
        return Promise.reject(new Error('Core validation failed'))
      }

      if (hasSyllabusChanges && !validateSection('syllabus', values)) {
        return Promise.reject(new Error('Syllabus validation failed'))
      }

      try {
        // Save course data
        let savedCourse = await saveCourse(
          values,
          hasCoreChanges,
          hasSyllabusChanges,
        )
        let uploadCourseID = savedCourse.id || courseID!

        // Upload images if needed
        let finalCourseID = await uploadImages(uploadCourseID, hasImageChanges)

        // Refresh courses list
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchCourses())

        // Handle success
        await handleSuccess(finalCourseID, sectionsToSave)

        return Promise.resolve()
      } catch (error) {
        handleError(error)
        return Promise.reject(error)
      }
    },
    [
      courseID,
      validateSection,
      saveCourse,
      uploadImages,
      dispatch,
      handleSuccess,
      handleError,
    ],
  )

  return { handleSubmit }
}
